/**
 * The IT Admin area's queries (5 Oct). Each read is a lookup the server logs
 * (who looked up whom), so profiles are not prefetched or refetched on focus.
 */

import { useQuery } from "@tanstack/react-query";

import {
  getItAdminPerson as sdkGetItAdminPerson,
  getItAdminPersonAccess as sdkGetItAdminPersonAccess,
  getTicketSearchOptions,
  type PersonProfileResponse,
  type ReferenceAccessResponse,
  searchTickets,
  type SearchTicketsData,
  type TicketSearchOptions,
  type TicketSearchResponse,
} from "@/lib/api";
import { logger } from "@/lib/logger";

export const itAdminKeys = {
  all: ["it-admin"] as const,
  person: (employeeId: string) => [...itAdminKeys.all, "person", employeeId] as const,
  personAccess: (employeeId: string) => [...itAdminKeys.all, "person-access", employeeId] as const,
  ticketSearchOptions: () => [...itAdminKeys.all, "ticket-search", "options"] as const,
  ticketSearch: (query: unknown) => [...itAdminKeys.all, "ticket-search", query] as const,
};

/** One person's profile: card, access by system, open tickets, named on, not visible. */
export function useItAdminPerson(employeeId: string) {
  return useQuery({
    queryKey: itAdminKeys.person(employeeId),
    queryFn: async (): Promise<PersonProfileResponse> => {
      logger.debug("[it-admin] person lookup", employeeId);
      const { data } = await sdkGetItAdminPerson({
        path: { employee_id: employeeId },
        throwOnError: true,
      });
      return data;
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * A person's access at a glance (21 Oct): one card per system, each entry
 * with its source. `enabled` false in the answer while the switch is off.
 */
export function useItAdminPersonAccess(employeeId: string) {
  return useQuery({
    queryKey: itAdminKeys.personAccess(employeeId),
    queryFn: async (): Promise<ReferenceAccessResponse> => {
      logger.debug("[it-admin] person access", employeeId);
      const { data } = await sdkGetItAdminPersonAccess({
        path: { employee_id: employeeId },
        throwOnError: true,
      });
      return data;
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Ticket search's pick lists (21 Oct): Ivanti's lists and the values in use, cached server-side. */
export function useTicketSearchOptions() {
  return useQuery({
    queryKey: itAdminKeys.ticketSearchOptions(),
    queryFn: async (): Promise<TicketSearchOptions> => {
      const { data } = await getTicketSearchOptions({ throwOnError: true });
      return data;
    },
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * One Ticket search (21 Oct). Runs only when `query` is set (the person pressed
 * Search); the same criteria again within a minute reuse the answer.
 */
export function useTicketSearch(query: SearchTicketsData["query"] | null) {
  return useQuery({
    queryKey: itAdminKeys.ticketSearch(query),
    queryFn: async (): Promise<TicketSearchResponse> => {
      logger.debug("[it-admin] ticket search", query);
      const { data } = await searchTickets({ query: query ?? {}, throwOnError: true });
      return data;
    },
    enabled: query !== null,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
